import React, { useState, useEffect } from "react";
import "./dashboard.scss";
import fitoja_logo from "../assets/fitoja-logo.png";
import fitoja_logo_background from "../assets/fitoja-logo-background.svg";
import googlePlayLogo from "../assets/google-play.png";
import appStoreLogo from "../assets/ios-app-store.png";
import { FaBars, FaTimes, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import VinduJiPic from "../assets/vindu_ji.png";
import dietPlan from "../assets/diet_plan.png";
import calorieTracker from "../assets/calorie_tracker.png";
import healthStore from "../assets/health_store.png";
import bmiTracker from "../assets/bmi_tracker.png";
import blogs from "../assets/blogs.png";
import WhatsappBanner from "./FloatingWhatsappBanner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ChatWidget from "../components/ChatWidget";
import MovingHeadline from "../components/moving_notification/MovingHeadline";
import parse from "html-react-parser";
import SocialMediaBar from "../components/social_media_icons/SocialMediaBar";
import TeamSlider from "../components/team_slider/TeamSlider";

const Dashboard = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const controls = useAnimation();
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [whyRef, whyInView] = useInView({ threshold: 0.1 });
  const [showHeader, setShowHeader] = useState(true); // Set to false to hide header
  const featuresControls = useAnimation();
  const navigate = useNavigate();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (featuresInView) {
      featuresControls.start("visible");
    }
  }, [featuresInView, featuresControls]);

  useEffect(() => {
    if (featuresInView) {
      controls.start("visible");
    }
  }, [controls, featuresInView]);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const featureVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const features = [
    {
      title: "Personalized Diet Plans",
      description:
        "Our AI-powered system creates customized diet plans based on your body type, health goals, dietary restrictions, and food preferences. Get weekly meal plans with recipes tailored just for you.",
      benefits: [
        "Weight loss/gain programs",
        "Medical condition-specific plans",
        "Vegan/vegetarian options",
      ],
      image: dietPlan,
      imagePosition: "left",
    },
    {
      title: "BMI & Progress Tracking",
      description:
        "Track your body metrics over time with our comprehensive dashboard. Visualize your progress with interactive charts and get insights to stay motivated on your health journey.",
      benefits: [
        "Body measurements tracking",
        "Progress photos timeline",
        "Customizable goal setting",
      ],
      image: bmiTracker,
      imagePosition: "right",
    },
    {
      title: "Calorie Tracking",
      description:
        "Log your meals effortlessly with our extensive food database. Get detailed nutritional breakdowns and smart suggestions to stay within your daily calorie targets.",
      benefits: [
        "Barcode scanner for foods",
        "Meal logging reminders",
        "Macronutrient tracking",
      ],
      image: calorieTracker,
      imagePosition: "left",
    },
    {
      title: "Ayurvedic Store",
      description:
        "Discover authentic Ayurvedic products curated by experts. From herbal supplements to skincare, find natural solutions for holistic well-being with detailed usage guides.",
      benefits: [
        "Certified organic products",
        "Dosha-specific recommendations",
        "Expert consultations",
      ],
      image: healthStore,
      imagePosition: "right",
    },
    {
      title: "Engaging Health Blogs",
      description:
        "Learn from certified nutritionists and Ayurvedic practitioners through our regularly updated content library. Evidence-based articles, recipes, and wellness tips at your fingertips.",
      benefits: [
        "New content weekly",
        "Expert Q&A sessions",
        "Community discussions",
      ],
      image: blogs,
      imagePosition: "left",
    },
    // {
    //   title: "User-Friendly Interface",
    //   description: "Enjoy a seamless experience with our intuitive design. Simple navigation, quick logging, and beautiful visuals make your health journey enjoyable and stress-free.",
    //   benefits: [
    //     "Dark/light mode",
    //     "Customizable dashboard",
    //     "Quick-access widgets"
    //   ],
    //   image: appScreenshot,
    //   imagePosition: "right"
    // }
  ];

  // 👇 ADD THESE NEW STATES
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planError, setPlanError] = useState(null);

  // Function to fetch subscription plans using Axios
  const fetchSubscriptionPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await axios.get(
        "https://api.fitoja.in/api/get-all-subscription-plans"
      );

      // Map and standardize the data from the API response
      const plans = response.data.data.map((plan) => ({
        planName: plan.planName || "Untitled Plan",
        duration: plan.duration || "Quarterly",
        price: plan.price || 0,
        isPopular: plan.isPopular || false,
        // Using a mock/default features list as the exact API structure for features is unknown
        features: [parse(plan.description)],
      }));
      setSubscriptionPlans(plans);
      setPlanError(null);
    } catch (error) {
      console.error("Failed to fetch subscription plans:", error);
      if (axios.isAxiosError(error) && error.response) {
        setPlanError(`Error fetching data: ${error.response.status}`);
      } else {
        setPlanError("Could not load subscription plans. Try again later.");
      }
      setSubscriptionPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    fetchSubscriptionPlans(); // 👈 Call fetch function here
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const SubscriptionPlans = () => {
    if (loadingPlans) {
      return (
        <section className="subscription-plans loading">
          <h2>Our Personalized Plans</h2>
          <div className="plan-slider-container">
            <p>Loading plans...</p>
          </div>
        </section>
      );
    }

    if (planError) {
      return (
        <section className="subscription-plans error">
          <h2>Our Personalized Plans</h2>
          <div className="plan-slider-container">
            <p className="error-message">{planError}</p>
          </div>
        </section>
      );
    }

    if (subscriptionPlans.length === 0) {
      return null;
    }

    return (
      <motion.section
        className="subscription-plans"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true, margin: "-50px" }}
      >
        <h2>Our Personalized Plans</h2>
        <div className="plan-slider-container">
          <div className="plan-slider">
            {subscriptionPlans.map((plan, index) => (
              <motion.div
                key={index}
                className={`plan-card ${plan.isPopular ? "popular" : ""}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                {plan.isPopular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                <h3>{plan.planName}</h3>
                <div className="plan-price">
                  <span>₹{plan.price}</span>
                  <span className="per-month">
                    / {plan.duration.toLowerCase()}
                  </span>
                </div>

                <ul className="plan-features">
                  {plan.features.map((feature, i) => (
                    <li key={i}>
                      {feature}
                    </li>
                  ))}
                </ul>
                {/* <a
                  className="cta-plan-btn"
                  style={{ textDecoration: "none", color: "white" }}
                  href={"tel:+919211388471"}
                >
                  Call Now
                </a> */}
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    );
  };

  return (
    <div className="dashboard">
      {/* Minimal Header or Remove it completely */}
      {/* {showHeader && (
        <header className={`header ${scrolled ? "scrolled" : ""}`}>
          <div className="logo">
            <img src={fitoja_logo} alt="Fitoja Logo" />
          </div>

          <div className="app-badges">
            <span
              className="contactUs-btn"
              onClick={() => navigate("/contact-us")}
              style={{ cursor: "pointer" }}
            >
              Contact Us
            </span>

            <Link
              className="showOnlyInWeb"
              to="https://play.google.com/store/apps/details?id=com.fitoja.fit"
              target="_blank"
            >
              <img src={googlePlayLogo} alt="Google Play" height="40" />
            </Link>
            <Link
              className="showOnlyInWeb"
              to="https://apps.apple.com/in/app/fitoja/id6740138984"
              target="_blank"
            >
              <img src={appStoreLogo} alt="App Store" height="40" />
            </Link>
          </div>
        </header>
      )} */}

      <MovingHeadline></MovingHeadline>
      <SocialMediaBar />

      {/* Completely Redesigned Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <div className="gradient-circle"></div>
          <div className="organic-shape"></div>
        </div>

        <div className="hero-content">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img className="fitoja_logo" src={fitoja_logo} alt="Fitoja Logo" />
              <h1>
                <span className="highlight">Fitoja</span>{" "}
              </h1>
            </span>
            <h1 style={{ textShadow: "0 0 10px darkseagreen", display: "contents" }}>
              Fitoja: Your Pocket Dietitian
            </h1>
            <br></br>
            <br></br>
            <h2 style={{ textShadow: "0 0 10px darkseagreen", display: "contents" }}>
              Fitness Means Fitoja!
            </h2>
            <p className="subhead">
              Fitoja combines Ayurveda and modern nutrition to create diet plans
              that truly work for you.
            </p>
            <div className="cta-container">
              <div className="app-stores">
                <div className="badges">
                  <Link
                    to="https://play.google.com/store/apps/details?id=com.fitoja.fit"
                    target="_blank"
                  >
                    <img src={googlePlayLogo} alt="Google Play" height="40" />
                  </Link>
                  <Link
                    to="https://apps.apple.com/in/app/fitoja/id6740138984"
                    target="_blank"
                  >
                    <img src={appStoreLogo} alt="App Store" height="40" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* <div className="phone-mockup"> */}
            <img
              className="appScreenshot"
              src={VinduJiPic}
              alt="Fitoja App Preview"
            />
            {/* </div> */}
            <div className="floating-elements">
              {/* <div className="floating-icon diet"></div> */}
              <div className="floating-icon bmi"></div>
              {/* <div className="floating-icon ayurveda"></div> */}
            </div>
          </motion.div>
        </div>
      </section>

      <SubscriptionPlans />

      {/* Features Section - Completely Redesigned */}
      <section className="features" ref={featuresRef}>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={featuresInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          Key Features of Fitoja
        </motion.h2>

        <div className="features-container">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={`feature-item ${feature.imagePosition}`}
              variants={featureVariants}
              initial="hidden"
              animate={featuresControls}
              custom={index}
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="feature-content">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <ul className="feature-benefits">
                  {feature.benefits.map((benefit, i) => (
                    <li key={i}>
                      <FaArrowRight className="benefit-icon" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="feature-image">
                <img src={feature.image} alt={feature.title} />
                <div className="image-overlay"></div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Fitoja Section */}
      <section className="why-choose" ref={whyRef}>
        <motion.div
          className="why-content"
          initial={{ opacity: 0, y: 20 }}
          animate={whyInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <h2>Why Choose Fitoja?</h2>
          <div className="why-cards">
            <div className="why-card">
              <h3>Holistic Approach</h3>
              <p>
                Combines modern nutrition science with traditional Ayurvedic
                practices for complete wellness.
              </p>
            </div>
            <div className="why-card">
              <h3>Community Support</h3>
              <p>
                Join a community of health-conscious individuals for motivation
                and shared success.
              </p>
            </div>
            <div className="why-card">
              <h3>Evidence-Based</h3>
              <p>
                All recommendations are backed by scientific research and
                nutritional expertise.
              </p>
            </div>
          </div>
          <div className="why-text">
            <p>
              Fitoja is a complete health app that goes beyond weight loss to
              promote overall well-being. It offers personalized diet plans
              combined with Ayurvedic principles to help users live healthier
              lives.
            </p>
            <p>
              The app also focuses on community support and education,
              encouraging users to take control of their health and build
              lasting habits. Whether you want to lose weight, manage a health
              condition, or improve your diet, Fitoja is designed to help you
              reach your goals.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Testimonials Section - New */}
      <section className="testimonials">
        <h2>What Our Users Say</h2>
        <div className="testimonial-cards">
          <div className="testimonial-card">
            <div className="quote">
              "Fitoja changed my relationship with food and health. Lost 15kg in
              3 months!"
            </div>
            <div className="author">- Priya K., Bangalore</div>
          </div>
          <div className="testimonial-card">
            <div className="quote">
              "The Ayurvedic integration is brilliant. Finally a diet app that
              understands Indian needs."
            </div>
            <div className="author">- Rohan M., Delhi</div>
          </div>
          <div className="testimonial-card">
            <div className="quote">
              "Easy to use and the personalized plans actually work. Highly
              recommended!"
            </div>
            <div className="author">- Anjali S., Mumbai</div>
          </div>
        </div>
      </section>

      <TeamSlider></TeamSlider>

      {/* CTA Section - New */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Transform Your Health?</h2>
          <p>Download Fitoja today and start your journey to better health</p>
          <div className="cta-buttons">
            <Link
              to="https://play.google.com/store/apps/details?id=com.fitoja.fit"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn"
            >
              <img
                className="google-play-logo"
                src={googlePlayLogo}
                alt="Google Play Logo"
              />
            </Link>
            <Link
              to="https://apps.apple.com/in/app/fitoja/id6740138984"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn"
            >
              <img
                className="app-store-logo"
                src={appStoreLogo}
                alt="App Store Logo"
              />
            </Link>
          </div>
        </div>
      </section>
      <WhatsappBanner />

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img src={fitoja_logo} alt="Fitoja Logo" />
            <p>Your personalized health and diet companion</p>
          </div>
          <div className="footer-links">
            <div className="link-group">
              <h3>Company</h3>
              <Link to="/about-us">About Us</Link>
              <Link to="/contact-us">Contact Us</Link>
              <Link to="/customer-support">Customer Support</Link>
            </div>
            <div className="link-group">
              <h3>Legal</h3>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-conditions">Terms & Conditions</Link>
              <Link to="/Sitemap">Sitemap</Link>
              {/* <Link to="/cookie-policy">Cookie Policy</Link> */}
            </div>
            <div className="link-group">
              <h3>Resources</h3>
              <Link to="/blogs">Health Blogs</Link>

              {/* <Link to="/faq">FAQ</Link>
              <Link to="/recipes">Recipes</Link> */}
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} Fitoja. All rights reserved. Designed
            for a healthier you.
          </p>
          <div className="social-links">
            <Link to="#" aria-label="Facebook">
              <i className="fab fa-facebook"></i>
            </Link>
            <Link to="#" aria-label="Instagram">
              <i className="fab fa-instagram"></i>
            </Link>
            <Link to="#" aria-label="Twitter">
              <i className="fab fa-twitter"></i>
            </Link>
            <Link to="#" aria-label="LinkedIn">
              <i className="fab fa-linkedin"></i>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
